import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryList } from '../../../Summary/SummaryList';

import { Q } from '../../../../store/student/types';
import { IRisk, OtherVisa, VisaRefusal } from '../../../../types';
import { SummaryListItem } from '../../../Summary/SummaryListItem';
import { getVisaLabelFromValue } from 'data';
import { useTranslation } from "react-i18next";

interface Props {
  showRisks: boolean;

  hasOtherUkVisas?: string;
  otherUkVisas?: Array<OtherVisa>;
  hasLeaveToRemain?: string;
  hasBorderRefusal?: string;
  borderRefusalReason?: string;
  hasUkVisaRefusals?: string;
  ukVisaRefusals?: Array<OtherVisa>;
  hasOtherVisaRefusals?: string;
  otherVisaRefusals?: Array<VisaRefusal>;
  riskList?: IRisk['historyRisk']['priorStudyAndRefusals'];
}

function PriorStudyAndTravel({
  showRisks,
  hasOtherUkVisas,
  otherUkVisas,
  hasLeaveToRemain,
  hasBorderRefusal,
  borderRefusalReason,
  hasUkVisaRefusals,
  ukVisaRefusals,
  hasOtherVisaRefusals,
  otherVisaRefusals,
  riskList = [],
}: Props) {
    const { t } = useTranslation('forms.visa');
  return (
    <>
      <SummaryRow
        key="otherVisa"
        label={t('forms.visa:hasOtherUkVisas')}
        fullLabel="Have you been granted any other visas (non-student) for the UK?"
        value={hasOtherUkVisas}
        isRisk={showRisks && riskList.includes(Q.PREV_UK_OTHER_VISA)}
      />
      {hasOtherUkVisas === 'Yes' && otherUkVisas?.length && (
        <>
          <SummaryRow
            key="otherVisaNumber"
            label={t('forms.visa:numberOfOtherUkVisas')}
            fullLabel="How many?"
            value={otherUkVisas?.length}
          />

          <SummaryList
            title={t('forms.visa:otherUkVisas')}
            tooltip={t('forms.visa:whatTypeOfVisaWereYouGrantedWhat')}
          >
            {otherUkVisas?.map((visa: OtherVisa, index: number) => {
              return (
                <SummaryListItem
                  value={getVisaLabelFromValue(visa.visaType)}
                  subvalue={visa.year}
                  key={`otherVisas-${index}`}
                />
              );
            })}
          </SummaryList>
        </>
      )}

      <SummaryRow
        key="leaveToRemain"
        label={t('forms.visa:leaveToRemain2')}
        fullLabel="Have you made an application to the Home Office to enter or remain in the UK in the last 10 years?"
        value={hasLeaveToRemain}
        isRisk={showRisks && riskList.includes(Q.UK_LEAVE_TO_REMAIN)}
      />

      <SummaryRow
        key="visaRejections"
        label={t('forms.visa:borderRefusals')}
        fullLabel="Have you been refused entry to the UK at the border?"
        value={hasBorderRefusal}
        isRisk={showRisks && riskList.includes(Q.PREVIOUS_VISA_REJECTION)}
      />

      {hasBorderRefusal === 'Yes' && (
        <SummaryRow label={t('forms.visa:moreInfo')} fullLabel="Please provide more information" value={borderRefusalReason} />
      )}

      <SummaryRow
        key="visaRefusal"
        label={t('forms.visa:hasVisaRefusals')}
        fullLabel="Have you ever previously received a visa refusal when attempting to travel to the UK?"
        value={hasUkVisaRefusals}
        isRisk={showRisks && riskList.includes(Q.PREV_VISA_REFUSAL)}
      />

      {hasUkVisaRefusals === 'Yes' && ukVisaRefusals?.length && (
        <>
          <SummaryRow
            key="visaRefusalNumber"
            label={t('forms.visa:numberOfVisaRefusals')}
            fullLabel="How many times?"
            value={ukVisaRefusals?.length}
          />

          <SummaryList
            title={t('forms.visa:visaRefusals')}
            tooltip={t('forms.visa:whatTypeOfVisaDidYouApplyForWhat')}
          >
            {ukVisaRefusals?.map((visa: OtherVisa, index: number) => {
              return (
                <SummaryListItem
                  key={`visaRefusals-${index}`}
                  value={getVisaLabelFromValue(visa.visaType)}
                  subvalue={visa.year}
                />
              );
            })}
          </SummaryList>
        </>
      )}

      <SummaryRow
        key="nonUKVisaRefusal"
        label={t('forms.visa:hasNonUkVisaRefusals')}
        fullLabel="Have you received a visa refusal from any other country (non-UK) in the last 10 years?"
        value={hasOtherVisaRefusals}
        isRisk={showRisks && riskList.includes(Q.PREV_NON_UK_VISA_REFUSAL)}
      />

      {hasOtherVisaRefusals === 'Yes' && otherVisaRefusals?.length && (
        <>
          <SummaryRow
            key="nonUKVisaRefusalNumber"
            label={t('forms.visa:numberOfNonUkVisaRefusals')}
            fullLabel="How many times?"
            value={otherVisaRefusals?.length}
          />

          <SummaryList
            title={t('forms.visa:nonUkVisaRefusals')}
            tooltip={t('forms.visa:whatCountryIssuedTheRefusalWasTh')}
          >
            {otherVisaRefusals?.map((visa: VisaRefusal, index: number) => {
              return (
                <SummaryListItem
                  value={visa.country}
                  subvalue={`${visa.year} - ${visa.isStudentVisa ? 'Student visa' : 'Non-student visa'}`}
                  key={`non-uk-visaRefusals-${index}`}
                />
              );
            })}
          </SummaryList>
        </>
      )}
    </>
  );
}

export default PriorStudyAndTravel;
